import { div } from './format.js';
import { cardKey } from './cards.js';
import { UNALLOCATED_LABEL, supplierLabel } from './model.js';
import { groupLabel } from './selection.js';

/* --------------------------------------------------------------------------
   Analys
   -------------------------------------------------------------------------- */

export function aggregate(){
  return {revenue:0, revenueEx:0, profit:0, units:0, tx:0, lines:0};
}
export function addTo(acc, rec){
  acc.revenue   += rec.revenue;
  acc.revenueEx += rec.revenueEx;
  acc.profit    += rec.profit;
  acc.units     += rec.units;
  acc.tx        += 1;
}

export function computeStats(receipts){
  const s = {
    tx: receipts.length,
    revenue:0, revenueEx:0, profit:0, units:0,
    paid:0, giftCardBooked:0, giftCardFace:0, giftCardCount:0, bonusChecks:0, roundOff:0,
    returnValue:0, returnUnits:0, returnReceipts:0,
    noCostGoods:{revenue:0, revenueEx:0, profit:0},
    noCostService:{revenue:0, revenueEx:0, profit:0},
    noCostArticles:new Set(), noCostServiceArticles:new Set(),
    multiLineTx:0,        // köp med fler än en artikel
    discountTotal:0,      // lämnad rabatt
    product:{revenue:0, revenueEx:0, profit:0, units:0, grossRevenue:0},
    service:{revenue:0, revenueEx:0, profit:0, units:0, grossRevenue:0},
    byDay:new Map(), byHour:new Map(), byWeekday:new Map(),
    byHourTx:new Map(), byWeekdayTx:new Map(), heat:new Map(),
    products:new Map(), suppliers:new Map(), group1:new Map(), groupFull:new Map(),
    stores:new Map(), cashiers:new Map(), payments:new Map(),
    // Kortbetalningar per korttyp. Nyckeln är cardKey(); '' är okänd korttyp
    // och null betalningar utan terminalkvitto.
    cards:new Map(), cardCount:0, cardWithTerminal:0, cardContactless:0, cardPin:0,
    delivery:new Map(),      // leveranssätt -> antal, omsättning, kopior
    deliveryByCashier:new Map(),
    extraCopies:0,
    returns:new Map(),
    // Kunder i lojalitetsprogrammet, per kundnummer
    custTx:0, custRevenue:0, customers:new Map(),
    // Inlösta bonuscheckar. Exporten har bara de inlösta, inte de utfärdade.
    bonusCount:0, bonusTx:0, bonusPaid:0, bonusFullCover:0, bonusNoCustomer:0,
    // Kampanjer per namn, och rabatt på enskilda artiklar (extrapris eller
    // rabatt i kassan — exporten skiljer inte på dem)
    promos:new Map(), promoTx:0, promoRevenue:0, promoDiscount:0,
    discArticles:new Map(), discTx:0, discRevenue:0, articleDiscount:0
  };

  for(const r of receipts){
    s.revenue     += r.revenue;
    s.revenueEx   += r.revenueEx;
    s.profit      += r.profit;
    s.units       += r.units;
    s.paid           += r.paid;
    s.giftCardBooked += r.giftCardBooked;
    s.giftCardFace   += r.giftCardFace;
    s.giftCardCount  += r.giftCardCount;
    s.bonusChecks    += r.bonusChecks;
    s.roundOff       += r.roundOff;
    ['noCostGoods','noCostService'].forEach(k => {
      s[k].revenue   += r[k].revenue;
      s[k].revenueEx += r[k].revenueEx;
      s[k].profit    += r[k].profit;
    });

    // Köp med mer än en artikel. Returrader ska inte göra ett köp "flerartikels",
    // därför räknas antalet sålda enheter, inte antalet rader.
    if(r.units > 1) s.multiLineTx += 1;

    if(r.hasReturn){
      s.returnReceipts += 1;
      s.returnValue    += r.returnValue;
      s.returnUnits    += r.returnUnits;
    }

    // Dag
    let d = s.byDay.get(r.day);
    if(!d){ d = {revenue:0, profit:0, tx:0}; s.byDay.set(r.day, d); }
    d.revenue += r.revenue; d.profit += r.profit; d.tx += 1;

    // Timme
    s.byHour.set(r.hour, (s.byHour.get(r.hour)||0) + r.revenue);
    s.byHourTx.set(r.hour, (s.byHourTx.get(r.hour)||0) + 1);

    // Veckodag
    let w = s.byWeekday.get(r.weekday);
    if(!w){ w = {revenue:0, profit:0, tx:0, units:0}; s.byWeekday.set(r.weekday, w); }
    w.revenue += r.revenue; w.profit += r.profit; w.tx += 1; w.units += r.units;

    // Heatmap veckodag × timme
    const hk = r.weekday + ':' + r.hour;
    s.heat.set(hk, (s.heat.get(hk)||0) + r.revenue);

    // Butik
    let st = s.stores.get(r.store);
    if(!st){ st = aggregate(); s.stores.set(r.store, st); }
    addTo(st, r);

    // Kassör
    let cs = s.cashiers.get(r.cashier);
    if(!cs){ cs = aggregate(); s.cashiers.set(r.cashier, cs); }
    addTo(cs, r);

    // Hur kvittot levererades
    let dl = s.delivery.get(r.delivery);
    if(!dl){ dl = {count:0, revenue:0, copies:0, withCustomer:0}; s.delivery.set(r.delivery, dl); }
    dl.count   += 1;
    dl.revenue += r.revenue;
    dl.copies  += r.copies;
    if(r.hasCustomer) dl.withCustomer += 1;
    s.extraCopies += r.copies;

    let dc = s.deliveryByCashier.get(r.cashier);
    if(!dc){ dc = {total:0, PRINTER:0, EMAIL:0, TRASH_CAN:0, UNKNOWN:0}; s.deliveryByCashier.set(r.cashier, dc); }
    dc.total += 1;
    dc[r.delivery] += 1;

    // Betalsätt
    for(const p of r.payments){
      let pay = s.payments.get(p.type);
      if(!pay){ pay = {amount:0, count:0}; s.payments.set(p.type, pay); }
      pay.amount += p.amount; pay.count += 1;
    }

    // Kort, med korttyp ur terminalens kvitto
    for(const c of r.cards){
      const key = c.terminal ? cardKey(c.label) : null;
      let e = s.cards.get(key);
      if(!e){ e = {amount:0, count:0, spellings:new Map()}; s.cards.set(key, e); }
      e.amount += c.amount; e.count += 1;
      if(c.label) e.spellings.set(c.label, (e.spellings.get(c.label) || 0) + 1);
      s.cardCount += 1;
      if(c.terminal){
        s.cardWithTerminal += 1;
        if(c.contactless) s.cardContactless += 1;
        if(c.pin) s.cardPin += 1;
      }
    }

    // Kund
    if(r.hasCustomer){
      s.custTx += 1;
      s.custRevenue += r.revenue;
      let c = s.customers.get(r.customer);
      if(!c){
        c = {number:r.customer, name:'', tx:0, revenue:0, profit:0, units:0, discount:0,
             bonusCount:0, bonus:0, first:r.day, last:r.day};
        s.customers.set(r.customer, c);
      }
      // Kvittona står i tidsordning: namnet blir det senaste kunden hade
      if(r.customerName) c.name = r.customerName;
      c.tx += 1; c.revenue += r.revenue; c.profit += r.profit; c.units += r.units;
      c.bonusCount += r.bonusCount; c.bonus -= r.bonusChecks;
      for(const ln of r.lines) c.discount += ln.discount;
      if(r.day < c.first) c.first = r.day;
      if(r.day > c.last)  c.last  = r.day;
    }

    // Bonuscheckar. Ett köp där kunden inte betalade något alls betalades helt
    // med checken; checken förbrukas då hel även om köpet var mindre.
    if(r.bonusCount){
      s.bonusCount += r.bonusCount;
      s.bonusTx    += 1;
      s.bonusPaid  += r.paid;
      if(Math.abs(r.paid) < 0.005) s.bonusFullCover += 1;
      if(!r.hasCustomer) s.bonusNoCustomer += 1;
    }

    // Rader
    let hasPromo = false, hasDisc = false;
    const promoRows = new Set();
    for(const ln of r.lines){
      if(ln.promoName != null){
        hasPromo = true;
        let pr = s.promos.get(ln.promoName);
        if(!pr){ pr = {name:ln.promoName, times:0, receipts:0, discount:0}; s.promos.set(ln.promoName, pr); }
        // En kampanj fördelad på tre artiklar är ett tillfälle, inte tre
        if(!promoRows.has(ln.promoRow)){ promoRows.add(ln.promoRow); pr.times += 1; }
        pr.discount += ln.discount;
        s.promoDiscount += ln.discount;
      }else if(ln.discount){
        hasDisc = true;
        let da = s.discArticles.get(ln.key);
        if(!da){
          da = {name:ln.name, articleNumber:ln.articleNumber, rows:0, units:0, discount:0, gross:0,
                first:r.day, last:r.day};
          s.discArticles.set(ln.key, da);
        }
        da.rows += 1; da.units += ln.q; da.discount += ln.discount;
        da.gross += ln.incVat + ln.discount;   // vad raden hade kostat utan rabatt
        if(r.day < da.first) da.first = r.day;
        if(r.day > da.last)  da.last  = r.day;
        s.articleDiscount += ln.discount;
      }
    }
    if(hasPromo){
      s.promoTx += 1; s.promoRevenue += r.revenue;
      for(const name of new Set(r.lines.filter(l => l.promoName != null).map(l => l.promoName))) s.promos.get(name).receipts += 1;
    }
    if(hasDisc){ s.discTx += 1; s.discRevenue += r.revenue; }

    for(const ln of r.lines){
      const bucket = ln.isService ? s.service : s.product;
      bucket.revenue   += ln.incVat;
      bucket.revenueEx += ln.exVat;
      bucket.profit    += ln.profit;
      bucket.units     += ln.q;
      // Bruttoomsättning räknar bara sålda rader, inte returer. Andelen utan
      // inköpspris måste mätas mot samma underlag för att kunna jämföras.
      if(ln.q > 0) bucket.grossRevenue += ln.incVat;

      let p = s.products.get(ln.key);
      if(!p){
        p = {name:ln.name, articleNumber:ln.articleNumber, isService:ln.isService,
             revenue:0, revenueEx:0, profit:0, units:0, discount:0, lastSold:'',
             receipts:0, noCost:false};
        s.products.set(ln.key, p);
      }
      p.revenue += ln.incVat; p.revenueEx += ln.exVat; p.profit += ln.profit;
      p.units += ln.q; p.discount += ln.discount;
      if(!ln.promo) p.receipts += 1;   // en kampanjfördelning är ingen egen försäljning
      if(ln.noCost){
        p.noCost = true;
        (ln.isService ? s.noCostServiceArticles : s.noCostArticles).add(ln.key);
      }
      s.discountTotal += ln.discount;
      if(r.day > p.lastSold) p.lastSold = r.day;

      accumulate(s.suppliers,  ln.supplierLabel, ln);
      accumulate(s.group1,     ln.unallocated ? UNALLOCATED_LABEL : groupLabel(ln.group1), ln);
      accumulate(s.groupFull,  ln.unallocated ? UNALLOCATED_LABEL : groupLabel(ln.group), ln);

      if(ln.q < 0){
        let rt = s.returns.get(ln.key);
        if(!rt){ rt = {name:ln.name, units:0, value:0, count:0}; s.returns.set(ln.key, rt); }
        rt.units += ln.q; rt.value += ln.incVat; rt.count += 1;
      }
    }
  }

  // Härledda nyckeltal
  s.aov         = div(s.revenue, s.tx);
  s.itemsPerTx  = div(s.units,   s.tx);
  s.margin      = div(s.profit,  s.revenueEx) * 100;
  s.days        = s.byDay.size;
  s.revenuePerDay = div(s.revenue, s.days);
  s.txPerDay      = div(s.tx, s.days);
  s.profitPerTx   = div(s.profit, s.tx);

  // Snittpris per såld artikel. Skiljer sig från snittköpet och visar om
  // sortimentet dragit mot dyrare eller billigare varor.
  s.avgUnitPrice = div(s.revenue, s.units);

  // Andel köp med mer än en artikel. Nyckeltal för merförsäljning.
  s.multiLineShare = div(s.multiLineTx, s.tx) * 100;

  // Returandel mätt mot bruttoförsäljningen, alltså före returavdrag.
  s.grossRevenue  = s.revenue + Math.abs(s.returnValue);
  s.returnShare   = div(Math.abs(s.returnValue), s.grossRevenue) * 100;

  // Rabattgrad mot försäljningen före rabatt.
  s.discountShare = div(s.discountTotal, s.revenue + s.discountTotal) * 100;

  // Samlat mått på hur mycket av vinsten som saknar registrerad kostnad.
  s.noCostRevenue   = s.noCostGoods.revenue   + s.noCostService.revenue;
  s.noCostRevenueEx = s.noCostGoods.revenueEx + s.noCostService.revenueEx;
  s.noCostProfit    = s.noCostGoods.profit    + s.noCostService.profit;
  s.noCostShare     = div(s.noCostProfit, s.profit) * 100;

  // Kvittoleverans. Andelarna är med i den vanliga statistiken så att de
  // kan följas över tid i utvecklingsvyn precis som övriga nyckeltal.
  const dGet = k => s.delivery.get(k)?.count || 0;
  s.printedReceipts = dGet('PRINTER');
  s.emailReceipts   = dGet('EMAIL');
  s.trashReceipts   = dGet('TRASH_CAN');
  s.emailShare = div(s.emailReceipts, s.tx) * 100;
  s.trashShare = div(s.trashReceipts, s.tx) * 100;
  s.printShare = div(s.printedReceipts, s.tx) * 100;
  // Varje utskrivet original plus varje extra kopia är ett papper
  s.paperSlips = s.printedReceipts + s.extraCopies;

  s.product.margin = div(s.product.profit, s.product.revenueEx) * 100;
  s.service.margin = div(s.service.profit, s.service.revenueEx) * 100;

  // Kunder
  s.customerCount     = s.customers.size;
  s.custShare         = div(s.custTx, s.tx) * 100;
  s.custRevenueShare  = div(s.custRevenue, s.revenue) * 100;
  s.custAov           = div(s.custRevenue, s.custTx);
  s.otherTx           = s.tx - s.custTx;
  s.otherAov          = div(s.revenue - s.custRevenue, s.otherTx);
  s.txPerCustomer     = div(s.custTx, s.customerCount);
  s.returningCustomers = [...s.customers.values()].filter(c => c.tx > 1).length;
  s.returningShare    = div(s.returningCustomers, s.customerCount) * 100;

  // Bonuscheckar: belopp som positivt tal, och vad kunden betalade utöver checken
  s.bonusAmount  = -s.bonusChecks;
  s.bonusPaidAvg = div(s.bonusPaid, s.bonusTx);
  s.bonusCustomers = [...s.customers.values()].filter(c => c.bonusCount > 0).length;
  s.bonusShareOfCustRevenue = div(s.bonusAmount, s.custRevenue) * 100;

  // Kampanjer och rabatt på artiklar
  s.promoShare = div(s.promoTx, s.tx) * 100;
  s.promoAov   = div(s.promoRevenue, s.promoTx);
  s.discShare  = div(s.discTx, s.tx) * 100;
  s.discAov    = div(s.discRevenue, s.discTx);

  // Hur stor del av varje kategoris försäljning som saknar inköpspris.
  // Mäts mot bruttoomsättningen, alltså bara sålda rader, eftersom täljaren
  // också bara räknar sålda rader. Annars kunde andelen överstiga 100 procent
  // i en period med många returer.
  s.grossSoldRevenue    = s.product.grossRevenue + s.service.grossRevenue;
  s.product.noCostShare = div(s.noCostGoods.revenue,   s.product.grossRevenue) * 100;
  s.service.noCostShare = div(s.noCostService.revenue, s.service.grossRevenue) * 100;
  s.noCostRevenueShare  = div(s.noCostRevenue,         s.grossSoldRevenue)     * 100;

  return s;
}

/** Från den här andelen av omsättningen utan leverantör påpekas det. */
export const SUPPLIER_GAP_PCT = 25;

/**
 * Hur stor del av omsättningen som saknar leverantör, när det är värt att
 * påpeka: från SUPPLIER_GAP_PCT procent men inte när ingen artikel alls har
 * leverantör — då har butiken valt att inte använda dem, och leverantörsfiltret
 * är redan avstängt. Ger {share, articles} eller null.
 */
export function supplierGap(s){
  const missing = s.suppliers.get(supplierLabel(''));
  if(!missing || !(s.revenue > 0) || missing.revenue >= s.revenue) return null;
  const share = div(missing.revenue, s.revenue) * 100;
  return share >= SUPPLIER_GAP_PCT ? {share, articles: missing.items.size} : null;
}

export function accumulate(map, key, ln){
  let e = map.get(key);
  if(!e){ e = {revenue:0, revenueEx:0, profit:0, units:0, items:new Set()}; map.set(key, e); }
  e.revenue += ln.incVat; e.revenueEx += ln.exVat; e.profit += ln.profit;
  e.units += ln.q; e.items.add(ln.key);
}
